import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Clock3,
  Crown,
  RefreshCcw,
  Search,
  ShieldAlert,
  Users,
  Wifi,
  WifiOff,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const API_URL = process.env.REACT_APP_API_URL || "";
const INITIAL_VISIBLE_ITEMS = 10;
const REVEAL_BATCH_SIZE = 10;

function numberFormat(value) {
  return Number(value || 0).toLocaleString("es-PE");
}

function formatDateTime(value) {
  if (!value) return "Sin actividad";
  try {
    return new Intl.DateTimeFormat("es-PE", {
      dateStyle: "short",
      timeStyle: "short",
      timeZone: "America/Lima",
    }).format(new Date(value));
  } catch (_) {
    return value;
  }
}

function shortPath(path) {
  if (!path) return "unknown";
  return String(path).replace(/^\/api\//, "").slice(0, 34);
}

function authConfig() {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

function MetricCard({ icon: Icon, label, value, sublabel, tone = "primary" }) {
  const toneClasses = {
    primary: "bg-brand-primary/10 text-brand-primary",
    green: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
    red: "bg-red-100 text-red-700",
    slate: "bg-slate-100 text-slate-700",
    gold: "bg-brand-gold/20 text-amber-700",
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
          <p className="mt-2 text-2xl font-extrabold text-slate-950">{value}</p>
          {sublabel && <p className="mt-1 text-xs text-slate-500">{sublabel}</p>}
        </div>
        <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${toneClasses[tone] || toneClasses.primary}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

function SectionCard({ title, description, children, action }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-extrabold text-slate-950">{title}</h2>
          {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function EmptyState({ label }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5 text-center text-sm text-slate-500">
      {label}
    </div>
  );
}

function StatusBadge({ status, stale }) {
  const config = {
    online: "border-emerald-200 bg-emerald-50 text-emerald-700",
    idle: "border-amber-200 bg-amber-50 text-amber-700",
    offline: "border-slate-200 bg-slate-100 text-slate-600",
  };
  const label = {
    online: "Online",
    idle: "Idle",
    offline: "Offline",
  }[status] || status || "unknown";

  return (
    <div className="flex flex-wrap gap-1">
      <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase ${config[status] || config.offline}`}>
        {label}
      </span>
      {stale && (
        <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-bold uppercase text-red-700">
          stale
        </span>
      )}
    </div>
  );
}

function ScrollableEventFeed({ events, emptyLabel }) {
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_ITEMS);
  const visibleEvents = (events || []).slice(0, visibleCount);
  const hasMore = visibleCount < (events || []).length;

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_ITEMS);
  }, [events]);

  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 90) {
      setVisibleCount((current) => Math.min(current + REVEAL_BATCH_SIZE, (events || []).length));
    }
  };

  if (!events?.length) return <EmptyState label={emptyLabel} />;

  return (
    <div
      className="max-h-[520px] divide-y divide-slate-100 overflow-y-auto pr-1"
      onScroll={handleScroll}
    >
      {visibleEvents.map((event) => (
        <div key={event.id} className="py-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-sm font-bold text-slate-950">{event.event_type || "unknown.event"}</p>
              <p className="mt-0.5 text-xs text-slate-500">
                {(event.actor_username || event.actor_user_id || "system")} · {event.actor_role || "unknown"}
                {event.path ? ` · ${event.path}` : ""}
              </p>
              {event.reason && <p className="mt-1 text-xs text-red-600">reason: {event.reason}</p>}
            </div>
            <div className="text-xs text-slate-500 sm:text-right">
              <p>{formatDateTime(event.created_at)}</p>
              {event.severity && <p className="mt-1 font-bold uppercase text-amber-700">{event.severity}</p>}
            </div>
          </div>
        </div>
      ))}
      {hasMore && (
        <div className="py-3 text-center text-xs font-semibold text-slate-400">
          Scroll to load {Math.min(REVEAL_BATCH_SIZE, events.length - visibleCount)} more
        </div>
      )}
    </div>
  );
}

function ActiveUsersTable({ users }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_ITEMS);

  const filteredUsers = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return (users || []).filter((user) => {
      const role = (user.role || "unknown").toLowerCase();
      const status = (user.status || "unknown").toLowerCase();

      const statusMatches =
        statusFilter === "all" ||
        status === statusFilter ||
        (statusFilter === "stale" && user.stale_session);
      const roleMatches = roleFilter === "all" || role === roleFilter;

      if (!statusMatches || !roleMatches) return false;
      if (!normalizedQuery) return true;

      const searchable = [
        user.id,
        user.username,
        user.email,
        user.role,
        user.status,
        user.stale_session ? "stale" : "",
        user.last_path,
        user.last_event_type,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(normalizedQuery);
    });
  }, [users, searchQuery, statusFilter, roleFilter]);

  const visibleUsers = filteredUsers.slice(0, visibleCount);
  const hasMore = visibleCount < filteredUsers.length;

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_ITEMS);
  }, [users, searchQuery, statusFilter, roleFilter]);

  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      setVisibleCount((current) => Math.min(current + REVEAL_BATCH_SIZE, filteredUsers.length));
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search users by name, email, role, status, route, or event"
            className="w-full rounded-xl border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm outline-none transition focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/10"
          />
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 outline-none focus:border-brand-primary"
          >
            <option value="all">All statuses</option>
            <option value="online">Online</option>
            <option value="idle">Idle</option>
            <option value="offline">Offline</option>
            <option value="stale">Stale</option>
          </select>
          <select
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value)}
            className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 outline-none focus:border-brand-primary"
          >
            <option value="all">All roles</option>
            <option value="customer">Customer</option>
            <option value="provider">Provider</option>
            <option value="driver">Driver</option>
            <option value="admin">Admin</option>
            <option value="master">Master</option>
          </select>
        </div>
      </div>

      <div className="mb-3 flex flex-col gap-1 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between">
        <span>
          Showing <strong className="text-slate-900">{numberFormat(Math.min(visibleCount, filteredUsers.length))}</strong> of{" "}
          <strong className="text-slate-900">{numberFormat(filteredUsers.length)}</strong> users
        </span>
        <span>{numberFormat(users?.length || 0)} loaded from current dashboard window</span>
      </div>

      {filteredUsers.length ? (
        <div className="overflow-hidden rounded-xl border border-slate-200">
          <div className="hidden grid-cols-[minmax(230px,1.4fr)_120px_140px_minmax(260px,1.7fr)_170px] gap-4 bg-slate-100 px-4 py-2 text-xs font-black uppercase tracking-wide text-slate-500 lg:grid">
            <span>User</span>
            <span>Role</span>
            <span>Status</span>
            <span>Last route / event</span>
            <span>Last activity</span>
          </div>
          <div
            className="max-h-[560px] divide-y divide-slate-100 overflow-y-auto"
            onScroll={handleScroll}
          >
            {visibleUsers.map((user) => (
              <div
                key={user.id}
                className="grid grid-cols-1 gap-3 px-4 py-3 text-sm lg:grid-cols-[minmax(230px,1.4fr)_120px_140px_minmax(260px,1.7fr)_170px] lg:items-center lg:gap-4"
              >
                <div>
                  <p className="font-bold text-slate-950">{user.username || `User ${user.id}`}</p>
                  <p className="break-all text-xs text-slate-500">{user.email || "Sin correo"}</p>
                </div>
                <div>
                  <span className="mb-1 block text-[10px] font-black uppercase tracking-wide text-slate-400 lg:hidden">
                    Role
                  </span>
                  <p className="font-semibold text-slate-700">{user.role || "unknown"}</p>
                </div>
                <div>
                  <span className="mb-1 block text-[10px] font-black uppercase tracking-wide text-slate-400 lg:hidden">
                    Status
                  </span>
                  <StatusBadge status={user.status} stale={user.stale_session} />
                </div>
                <div>
                  <span className="mb-1 block text-[10px] font-black uppercase tracking-wide text-slate-400 lg:hidden">
                    Last route / event
                  </span>
                  <p className="break-all text-xs font-semibold text-slate-600">
                    {user.last_path || user.last_event_type || "Sin evento"}
                  </p>
                </div>
                <div>
                  <span className="mb-1 block text-[10px] font-black uppercase tracking-wide text-slate-400 lg:hidden">
                    Last activity
                  </span>
                  <p className="text-xs font-semibold text-slate-600">{formatDateTime(user.last_active_at)}</p>
                </div>
              </div>
            ))}
            {hasMore && (
              <div className="bg-slate-50 py-3 text-center text-xs font-semibold text-slate-400">
                Scroll to load {Math.min(REVEAL_BATCH_SIZE, filteredUsers.length - visibleCount)} more users
              </div>
            )}
          </div>
        </div>
      ) : (
        <EmptyState label="No users matched this search/filter." />
      )}
    </div>
  );
}

export default function MasterIntelligenceDashboard({ username, setActiveComponent }) {
  const [selectedWindow, setSelectedWindow] = useState("24h");
  const [roleFilter, setRoleFilter] = useState("all");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API_URL}/api/master/intelligence-dashboard`, {
        ...authConfig(),
        params: { window: selectedWindow, role: roleFilter },
      });
      setDashboard(data);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "No se pudo cargar Executive Intelligence."
      );
    } finally {
      setLoading(false);
    }
  }, [selectedWindow, roleFilter]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const presence = dashboard?.presence || {};
  const sessionMetrics = dashboard?.session_metrics || {};

  const roleActivity = useMemo(() => {
    const byRole = presence.by_role || {};
    const rolesFromPresence = Object.entries(byRole)
      .filter(([role]) => roleFilter === "all" || role === roleFilter)
      .map(([role, metrics]) => ({
        role,
        online: metrics.online || 0,
        idle: metrics.idle || 0,
        offline: metrics.offline || 0,
        stale: metrics.stale || 0,
        total: metrics.total || 0,
      }));

    if (rolesFromPresence.length) return rolesFromPresence;

    return (dashboard?.activity_by_role || []).map((item) => ({
      role: item.role,
      online: 0,
      idle: 0,
      offline: 0,
      stale: 0,
      total: item.events || 0,
    }));
  }, [dashboard, presence.by_role, roleFilter]);

  const topRoutes = useMemo(
    () =>
      (dashboard?.top_routes || []).map((route) => ({
        ...route,
        label: shortPath(route.path),
      })),
    [dashboard]
  );

  const onlineTotal = presence.online || 0;
  const idleTotal = presence.idle || 0;
  const offlineTotal = presence.offline || 0;
  const staleTotal = presence.stale || 0;
  const activeUsers = presence.users || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-950 text-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setActiveComponent("MasterHome")}
              className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10 hover:bg-white/20"
              aria-label="Volver"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-gold/20">
              <Crown className="h-6 w-6 text-brand-gold" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold">Executive Intelligence</h1>
              <p className="text-sm text-white/70">
                Master session, security, behavior, and presence intelligence · {username || "Master"}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <select
              value={selectedWindow}
              onChange={(event) => setSelectedWindow(event.target.value)}
              className="rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-sm font-semibold text-white outline-none"
            >
              <option className="text-slate-950" value="24h">
                Last 24h
              </option>
              <option className="text-slate-950" value="7d">
                Last 7 days
              </option>
            </select>
            <select
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value)}
              className="rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-sm font-semibold text-white outline-none"
            >
              <option className="text-slate-950" value="all">
                All roles
              </option>
              <option className="text-slate-950" value="customer">
                Customers
              </option>
              <option className="text-slate-950" value="provider">
                Providers
              </option>
              <option className="text-slate-950" value="driver">
                Drivers
              </option>
              <option className="text-slate-950" value="admin">
                Admins
              </option>
              <option className="text-slate-950" value="master">
                Master
              </option>
            </select>
            <button
              type="button"
              onClick={loadDashboard}
              disabled={loading}
              className="flex items-center justify-center gap-2 rounded-xl bg-brand-primary px-4 py-2 text-sm font-bold text-white transition hover:bg-brand-primary/90 disabled:opacity-60"
            >
              <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-4 py-6">
        {error && (
          <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
            {error}
          </div>
        )}

        <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-wide text-brand-primary">Operational truth layer</p>
              <h2 className="mt-1 text-lg font-extrabold text-slate-950">
                Presence and behavior derived from operational_events
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Online = activity under 5 minutes. Idle = 5-30 minutes. Stale = no activity for 24+ hours.
              </p>
            </div>
            <div className="text-sm text-slate-500 md:text-right">
              <p>Generated: {formatDateTime(dashboard?.generated_at)}</p>
              <p>Window: {dashboard?.window || selectedWindow} · Role: {dashboard?.role_filter || roleFilter}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
          <MetricCard icon={Wifi} label="Online users" value={numberFormat(onlineTotal)} sublabel="Last 5 minutes" tone="green" />
          <MetricCard icon={Clock3} label="Idle users" value={numberFormat(idleTotal)} sublabel="5-30 minutes" tone="amber" />
          <MetricCard icon={WifiOff} label="Offline users" value={numberFormat(offlineTotal)} sublabel="Older than 30 minutes" tone="slate" />
          <MetricCard icon={AlertTriangle} label="Stale sessions" value={numberFormat(staleTotal)} sublabel="24+ hours inactive" tone="red" />
          <MetricCard icon={ShieldAlert} label="Denied access" value={numberFormat(sessionMetrics.denied_access)} sublabel="Security route denials" tone="red" />
          <MetricCard icon={Activity} label="Failed logins" value={numberFormat(sessionMetrics.failed_logins)} sublabel="Invalid credentials" tone="amber" />
        </div>

        <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-3">
          <SectionCard
            title="Hourly activity"
            description="Access, session, and security event volume by hour."
            action={<BarChart3 className="h-5 w-5 text-brand-primary" />}
          >
            {(dashboard?.activity_timeline || []).length ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dashboard.activity_timeline}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="events" stroke="#0f172a" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="sessions" stroke="#2563eb" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="security" stroke="#dc2626" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyState label="No hourly activity data yet." />
            )}
          </SectionCard>

          <SectionCard title="Presence by role" description="Online, idle, offline, and stale counts by actor role.">
            {roleActivity.length ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={roleActivity}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="role" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="online" stackId="presence" fill="#059669" />
                    <Bar dataKey="idle" stackId="presence" fill="#d97706" />
                    <Bar dataKey="offline" stackId="presence" fill="#64748b" />
                    <Bar dataKey="stale" fill="#dc2626" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyState label="No role activity data yet." />
            )}
          </SectionCard>

          <SectionCard title="Top accessed routes" description="Most frequent protected API route access/denial paths.">
            {topRoutes.length ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topRoutes} layout="vertical" margin={{ left: 12, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="label" width={115} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value, name, props) => [value, props?.payload?.path || name]} />
                    <Bar dataKey="count" fill="#2563eb" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyState label="No route access events yet." />
            )}
          </SectionCard>
        </div>

        <div className="mt-5">
          <SectionCard
            title="Active users"
            description="Full-width searchable presence table for users, providers, drivers, admins, and master actors."
          >
            <ActiveUsersTable users={activeUsers} />
          </SectionCard>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-2">
          <SectionCard title="Security events" description="Failed login, denied access, invalid token, and expired session signals.">
            <ScrollableEventFeed events={dashboard?.security_events || []} emptyLabel="No security events in this window." />
          </SectionCard>

          <SectionCard title="Recent activity stream" description="Latest access and operational behavior events feeding the master view.">
            <ScrollableEventFeed events={dashboard?.recent_activity || []} emptyLabel="No recent activity yet." />
          </SectionCard>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-4">
          <SectionCard title="Top active users" description="Meaningful events, excluding heartbeat-only noise where possible.">
            {(dashboard?.top_users || []).length ? (
              <div className="divide-y divide-slate-100">
                {dashboard.top_users.map((user) => (
                  <div key={user.id} className="flex items-center justify-between gap-3 py-3">
                    <div>
                      <p className="text-sm font-bold text-slate-950">{user.username || `User ${user.id}`}</p>
                      <p className="text-xs text-slate-500">{user.role || "unknown"}</p>
                    </div>
                    <span className="rounded-full bg-slate-950 px-3 py-1 text-xs font-bold text-white">
                      {numberFormat(user.events)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState label="No active user events yet." />
            )}
          </SectionCard>

          <SectionCard title="Top providers" description="Provider-level actor activity from operational events.">
            {(dashboard?.top_providers || []).length ? (
              <div className="divide-y divide-slate-100">
                {dashboard.top_providers.map((provider) => (
                  <div key={provider.provider_id} className="flex items-center justify-between gap-3 py-3">
                    <div>
                      <p className="text-sm font-bold text-slate-950">{provider.provider_name}</p>
                      <p className="text-xs text-slate-500">Provider #{provider.provider_id}</p>
                    </div>
                    <span className="rounded-full bg-brand-primary px-3 py-1 text-xs font-bold text-white">
                      {numberFormat(provider.events)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState label="No provider events yet." />
            )}
          </SectionCard>

          <SectionCard title="Top drivers" description="Driver-level activity from protected route and operational events.">
            {(dashboard?.top_drivers || []).length ? (
              <div className="divide-y divide-slate-100">
                {dashboard.top_drivers.map((driver) => (
                  <div key={driver.driver_user_id} className="flex items-center justify-between gap-3 py-3">
                    <div>
                      <p className="text-sm font-bold text-slate-950">{driver.driver_name}</p>
                      <p className="text-xs text-slate-500">Driver user #{driver.driver_user_id}</p>
                    </div>
                    <span className="rounded-full bg-emerald-600 px-3 py-1 text-xs font-bold text-white">
                      {numberFormat(driver.events)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState label="No driver events yet." />
            )}
          </SectionCard>

          <SectionCard title="Session health" description="Login, refresh, logout, expired-token, and invalid-token event counts.">
            <div className="grid grid-cols-2 gap-3">
              {[
                ["Logins", sessionMetrics.logins],
                ["Refreshes", sessionMetrics.refreshes],
                ["Logouts", sessionMetrics.logouts],
                ["Token expired", sessionMetrics.token_expired],
                ["Session expired", sessionMetrics.session_expired],
                ["Invalid/missing", sessionMetrics.invalid_or_missing_token],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</p>
                  <p className="mt-2 text-xl font-black text-slate-950">{numberFormat(value)}</p>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>

        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-500 shadow-sm">
          <div className="flex items-center gap-2 font-bold uppercase tracking-wide text-slate-700">
            <Users className="h-4 w-4" />
            Data assumptions
          </div>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {(dashboard?.data_assumptions || [
              "Presence is derived from operational events, not direct socket state.",
              "No secrets, tokens, passwords, or message contents are included.",
            ]).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </main>
    </div>
  );
}
